import { useCallback, useEffect, useRef, useState } from "react";
import IdleTimer from "react-idle-timer";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AuthLogout } from "../api/action/user";
import keys from "../config/keys.jsx";
import { academyLogger } from "utils/logger";
import {
  ACCESS_ISSUED_AT_KEY,
  ACCESS_REFRESHED_EVENT,
  ACCESS_TOKEN_TTL_MS,
  REFRESH_LEAD_TIME_MS,
  SESSION_EXPIRED_EVENT,
  getAccessIssuedAt,
  hasAuthenticatedSession,
  isAccessTokenNearExpiry,
  markAccessTokenIssued,
  refreshAuthSession,
} from "../auth/sessionCoordinator";

Modal.setAppElement("#root");

const IDLE_TIMEOUT = 15 * 60 * 1000;
const LOGOUT_COUNTDOWN = 30;

const isPageActive = () => (
  typeof document !== "undefined"
  && document.visibilityState === "visible"
  && (typeof document.hasFocus !== "function" || document.hasFocus())
);

const IdleTimerContainer = () => {
  const idleTimerRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const authenticatedRef = useRef(false);
  const logoutInitiatedRef = useRef(false);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [countdown, setCountdown] = useState(LOGOUT_COUNTDOWN);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated === true);

  useEffect(() => {
    authenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const cleanupTimers = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const expireSession = useCallback(async (reason = "authentication-failed") => {
    if (logoutInitiatedRef.current) return;
    if (!authenticatedRef.current && !hasAuthenticatedSession()) return;

    logoutInitiatedRef.current = true;
    cleanupTimers();
    setModalIsOpen(false);

    try {
      await dispatch(AuthLogout());
    } catch (error) {
      academyLogger.error("Session cleanup error:", error);
    } finally {
      try {
        window.localStorage.setItem("isTokenExpired", "true");
      } catch {
        // The redirect remains the source of truth if storage is unavailable.
      }

      navigate("/sign-in", {
        replace: true,
        state: { isTokenExpired: true, reason },
      });
    }
  }, [cleanupTimers, dispatch, navigate]);

  const refreshIfActive = useCallback(async () => {
    if (!authenticatedRef.current || logoutInitiatedRef.current || !isPageActive()) return false;
    if (idleTimerRef.current?.isIdle?.()) return false;
    if (!isAccessTokenNearExpiry()) return true;

    const refreshed = await refreshAuthSession(keys.URL, { force: true });
    if (!refreshed) {
      await expireSession("refresh-failed");
      return false;
    }

    return true;
  }, [expireSession]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (!authenticatedRef.current || logoutInitiatedRef.current) return;

    const issuedAt = getAccessIssuedAt() || markAccessTokenIssued();
    const refreshAt = issuedAt + ACCESS_TOKEN_TTL_MS - REFRESH_LEAD_TIME_MS;
    const delay = Math.max(0, refreshAt - Date.now());

    refreshTimeoutRef.current = window.setTimeout(async () => {
      refreshTimeoutRef.current = null;

      if (!authenticatedRef.current || logoutInitiatedRef.current) return;
      if (!isPageActive() || idleTimerRef.current?.isIdle?.()) return;

      const refreshed = await refreshIfActive();
      if (refreshed) scheduleRefresh();
    }, delay);
  }, [refreshIfActive]);

  const updateLastActivity = useCallback(() => {
    if (isPageActive()) {
      lastActivityRef.current = Date.now();
    }
  }, []);

  const onIdle = useCallback(() => {
    if (!authenticatedRef.current || logoutInitiatedRef.current) return;

    cleanupTimers();
    setModalIsOpen(true);
    setCountdown(LOGOUT_COUNTDOWN);

    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((previous) => {
        if (previous <= 1) {
          void expireSession("idle-timeout");
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  }, [cleanupTimers, expireSession]);

  const onActive = useCallback(() => {
    if (!authenticatedRef.current || logoutInitiatedRef.current) return;

    updateLastActivity();
    if (modalIsOpen) {
      setModalIsOpen(false);
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    void refreshIfActive().finally(scheduleRefresh);
  }, [modalIsOpen, refreshIfActive, scheduleRefresh, updateLastActivity]);

  useEffect(() => {
    if (!isAuthenticated) {
      cleanupTimers();
      return undefined;
    }

    logoutInitiatedRef.current = false;
    updateLastActivity();
    if (!getAccessIssuedAt()) markAccessTokenIssued();
    scheduleRefresh();

    return cleanupTimers;
  }, [cleanupTimers, isAuthenticated, scheduleRefresh, updateLastActivity]);

  useEffect(() => {
    const handleActivity = () => {
      if (!authenticatedRef.current || !isPageActive()) return;
      updateLastActivity();
    };

    const handleVisibilityOrFocus = () => {
      if (!authenticatedRef.current || !isPageActive()) return;

      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT) {
        onIdle();
        return;
      }

      updateLastActivity();
      void refreshIfActive().finally(scheduleRefresh);
    };

    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [onIdle, refreshIfActive, scheduleRefresh, updateLastActivity]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      void expireSession(event.detail?.reason || "authentication-failed");
    };

    const handleStorage = (event) => {
      if (event.key === "avpsLoggedOut" || event.key === "tokenExpired") {
        void expireSession("session-ended-in-another-tab");
        return;
      }

      if (event.key === ACCESS_ISSUED_AT_KEY && authenticatedRef.current) {
        scheduleRefresh();
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener("storage", handleStorage);
    window.addEventListener(ACCESS_REFRESHED_EVENT, scheduleRefresh);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(ACCESS_REFRESHED_EVENT, scheduleRefresh);
    };
  }, [expireSession, scheduleRefresh]);

  if (!isAuthenticated) return null;

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => {
          idleTimerRef.current?.reset?.();
          onActive();
        }}
        className="idle-modal"
        overlayClassName="idle-overlay"
        style={{
          overlay: {
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <div className="idle-modal-content">
          <h2>Session About to Expire</h2>
          <p>You will be automatically logged out in {countdown} seconds.</p>
          <div className="idle-modal-actions">
            <button className="idle-logout-btn" onClick={() => void expireSession("user-logout")}>
              Logout Now
            </button>
            <button
              className="idle-stay-btn"
              onClick={() => {
                idleTimerRef.current?.reset?.();
                onActive();
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      </Modal>

      <IdleTimer
        ref={idleTimerRef}
        timeout={IDLE_TIMEOUT}
        onIdle={onIdle}
        onActive={onActive}
        crossTab={{ emitOnAllTabs: true }}
        debounce={500}
      />

      <style jsx global>{`
        .idle-modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          padding: 2rem;
          border-radius: 8px;
          max-width: 450px;
          width: 90%;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          z-index: 1001;
          border: none;
          outline: none;
        }

        .idle-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 1000;
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
        }

        .idle-modal-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .idle-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1rem;
        }

        .idle-logout-btn,
        .idle-stay-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .idle-logout-btn {
          background: #f5f5f5;
          border: 1px solid #ddd;
        }

        .idle-logout-btn:hover {
          background: #eaeaea;
        }

        .idle-stay-btn {
          background: #1890ff;
          color: white;
          border: none;
        }

        .idle-stay-btn:hover {
          background: #40a9ff;
        }

        .ReactModal__Content--after-open {
          animation: modalAppear 0.3s ease-out forwards;
        }

        @keyframes modalAppear {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default IdleTimerContainer;
